import { useEffect } from 'react'

import { ProductV2, GameId, isGameId } from '@common'
import { StoreBonusStatusResponse } from '@common/models/bonus'
import { useRouter } from 'next/router'

import { useBonusStatusQuery } from '@/api'
import { useModalContext } from '@/contexts/ModalContext'
import { Game } from '@/fixtures/games'
import { hasCompletedOnboarding } from '@/utils/cookies'
import { useAppSelector } from '@/utils/useAppSelector'

import { Header } from './_storeBase/header/Header'
import { StorefrontWrapper } from './_storeBase/structure/StorefrontWrapper'
import BrawlStarsStoreFront from './brawlstars'
import ClashOfClansStoreFront from './clashofclans'
import ClashRoyaleStoreFront from './clashroyale'
import brawlScreens from './landing/Onboarding/screens/brawlstars'
import clashScreens from './landing/Onboarding/screens/clash-of-clans'
import clashRoyaleScreens from './landing/Onboarding/screens/clash-royale'
import { Maintenance } from './maintenance'
import SquadBustersStoreFront from './squadbusters'
import { config } from '../../../config'
import { LOGIN_STATUS } from '../user/userSlice'

type GameStoreProps = {
  game: Game
  products: ProductV2[]
  serverLoginStatus: LOGIN_STATUS
  enableNewVisuals?: GameId[]
}

type GameStoreFrontProps = GameStoreProps & {
  products: ProductV2[]
  bonusStatus?: StoreBonusStatusResponse
}

const getScreens = (gameSlug: GameId) => {
  if (gameSlug === 'brawlstars') {
    return brawlScreens
  } else if (gameSlug === 'clashroyale') {
    return clashRoyaleScreens
  }
  return clashScreens
}

const enabledGames = config.ENABLED_NEW_STOREFRONTS.filter(isGameId)

// poll bonuStatus every 5 minutes
const POLLING_INTERVAL = 5 * 60 * 1000

export function GameStore({ game, products, serverLoginStatus, enableNewVisuals }: GameStoreProps) {
  const router = useRouter()
  const loginStatus = useAppSelector((state) => state.user.loginStatus)
  const profile = useAppSelector((state) => state.user?.profile)
  const { openModal } = useModalContext()
  const claimedBonusItems = useAppSelector((state) => state.common.claimedBonusItems)
  const inMaintenance = useAppSelector((state) => state.config.inMaintenance[game.slug])

  // skip bonus status query if user has no current game in profile, prevent scroll to top when product modal is opened
  const missingCurrentGameFromProfile = !profile?.applications?.some(
    (app) => app.application === game.slug && app.account,
  )

  const { data: statefulBonusStatus, refetch: refetchBonusStatus } = useBonusStatusQuery(game.slug, {
    skip: loginStatus !== LOGIN_STATUS.LOGGED_IN || missingCurrentGameFromProfile,
    pollingInterval: POLLING_INTERVAL,
  })

  const onboardingCompleted = hasCompletedOnboarding()

  useEffect(() => {
    // Server had no user but had the sso cookie, sso client loggs in
    if (serverLoginStatus === LOGIN_STATUS.SSO_LOGIN_MIGHT_HAPPEN && loginStatus === LOGIN_STATUS.LOGGED_IN) {
      router.reload()
    }

    // Server had user, sso client logs out
    if (loginStatus === LOGIN_STATUS.LOGGED_OUT && serverLoginStatus === LOGIN_STATUS.LOGGED_IN) {
      router.reload()
    }

    // Server had no user, sso client logs in
    if (loginStatus === LOGIN_STATUS.LOGGED_IN && serverLoginStatus === LOGIN_STATUS.LOGGED_OUT) {
      router.reload()
    }
  }, [serverLoginStatus, loginStatus, router])

  const screens = getScreens(game.slug)

  useEffect(() => {
    if (enabledGames.includes(game.slug) && !onboardingCompleted) {
      openModal({ type: 'ONBOARDING', props: { gameId: game.slug, screens: screens.onboarding } })
    } else if (enabledGames.includes(game.slug) && onboardingCompleted && loginStatus === LOGIN_STATUS.LOGGED_OUT) {
      openModal({ type: 'ONBOARDING_LOGIN', props: { gameId: game.slug, screen: screens.login } })
    }
  }, [openModal, game, onboardingCompleted, loginStatus, screens])

  useEffect(() => {
    if (claimedBonusItems.length > 0) {
      refetchBonusStatus()
    }
  }, [claimedBonusItems, refetchBonusStatus])

  useEffect(() => {
    const endTimeMillis = statefulBonusStatus?.endTimeMillis
    if (endTimeMillis) {
      const now = Date.now()
      if (endTimeMillis < now) {
        // Fetch bonus status if season has ended
        refetchBonusStatus()
      } else {
        // Fetch bonus status one second after season ends
        const timeUntilSeasonEnd = endTimeMillis - now
        const timeout = setTimeout(() => refetchBonusStatus(), timeUntilSeasonEnd + 1000)
        return () => clearTimeout(timeout)
      }
    }
  }, [statefulBonusStatus, refetchBonusStatus])

  return (
    <StorefrontWrapper gameId={game.slug}>
      <Header game={game.slug} />
      {inMaintenance ? (
        <Maintenance gameId={game.slug} reason="maintenance" />
      ) : products.length === 0 ? (
        <Maintenance gameId={game.slug} reason="no_products" />
      ) : (
        <GameStoreFront
          game={game}
          products={products}
          bonusStatus={statefulBonusStatus}
          serverLoginStatus={serverLoginStatus}
          enableNewVisuals={enableNewVisuals}
        />
      )}
    </StorefrontWrapper>
  )
}

function GameStoreFront(props: GameStoreFrontProps) {
  switch (props.game.slug) {
    case 'brawlstars':
      return <BrawlStarsStoreFront {...props} />
    case 'clashofclans':
      return <ClashOfClansStoreFront {...props} />
    case 'clashroyale':
      return <ClashRoyaleStoreFront {...props} />
    case 'squadbusters':
      return <SquadBustersStoreFront {...props} />
    default:
      return null
  }
}
